import React, { useState, useMemo, useEffect } from "react";
import { View, ScrollView, TextInput, Pressable, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { UserType } from "@/types/db/users";
import { userService } from "@/services/userService";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import Avatar from "@/components/Avatar";

export type UserPickerProps = {
  selectedUserIds?: string[];
  excludeUserIds?: string[];
  onPick?: (user: UserType) => void;
  onMultiPick?: (users: UserType[]) => void;
  onCancel?: () => void;
  multiple?: boolean;
  title?: string;
};

export function UserPicker({
  selectedUserIds = [],
  excludeUserIds = [],
  onPick,
  onMultiPick,
  onCancel,
  multiple = false,
  title,
}: UserPickerProps) {
  const { colors } = useTheme();
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState<UserType[]>([]);

  // Fetch users on mount
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const data = await userService.getUsers();
        setUsers(data);

        // Pre-select users if IDs provided
        if (selectedUserIds.length > 0) {
          const preSelected = data.filter((u) => selectedUserIds.includes(u.id!));
          setSelectedUsers(preSelected);
        }
      } catch (error) {
        console.error("Failed to fetch users:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredUsers = useMemo(() => {
    // First exclude any users in excludeUserIds
    let result = excludeUserIds.length > 0 ? users.filter((u) => !excludeUserIds.includes(u.id!)) : users;

    // Then apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(
        (user) =>
          user.first_name?.toLowerCase().includes(searchLower) ||
          user.last_name?.toLowerCase().includes(searchLower) ||
          user.email_address?.toLowerCase().includes(searchLower) ||
          user.preferred_name?.toLowerCase().includes(searchLower),
      );
    }
    return result;
  }, [users, search, excludeUserIds]);

  const handleUserSelect = (user: UserType) => {
    if (multiple) {
      const isSelected = selectedUsers.some((u) => u.id === user.id);
      if (isSelected) {
        setSelectedUsers(selectedUsers.filter((u) => u.id !== user.id));
      } else {
        setSelectedUsers([...selectedUsers, user]);
      }
    } else {
      onPick?.(user);
    }
  };

  const handleConfirmMultiple = () => {
    onMultiPick?.(selectedUsers);
  };

  const renderUser = (user: UserType) => {
    const displayName = user.preferred_name || user.first_name;
    const fullName = `${displayName} ${user.last_name}`.trim();
    const isSelected = selectedUsers.some((u) => u.id === user.id);

    return (
      <Pressable
        key={user.id}
        testID={`user-option-${user.id}`}
        style={[
          styles.userItem,
          { borderBottomColor: colors.border },
          multiple && isSelected && { backgroundColor: colors.primary + "10" },
        ]}
        onPress={() => handleUserSelect(user)}
      >
        <View style={styles.userContent}>
          <Avatar
            userId={user.id!}
            firstName={user.preferred_name || user.first_name || "Unknown"}
            lastName={user.last_name || "User"}
            size={36}
          />
          <View style={styles.userInfo}>
            <ThemedText type="defaultSemiBold">{fullName || "Unknown User"}</ThemedText>
            {user.email_address && <ThemedText style={styles.userDetail}>{user.email_address}</ThemedText>}
          </View>
          {multiple && (
            <MaterialCommunityIcons
              name={isSelected ? "checkbox-marked" : "checkbox-blank-outline"}
              size={24}
              color={isSelected ? colors.primary : colors.text}
            />
          )}
        </View>
      </Pressable>
    );
  };

  return (
    <View style={styles.container}>
      <ThemedText type="subtitle">{title || (multiple ? "Select Users" : "Select User")}</ThemedText>

      <TextInput
        testID="user-search-input"
        style={[
          styles.searchInput,
          {
            borderColor: colors.border,
            color: colors.text,
            backgroundColor: colors.backgroundSecondary,
          },
        ]}
        placeholder="Search by name or email..."
        placeholderTextColor={colors.placeholder}
        value={search}
        onChangeText={setSearch}
        autoFocus
        editable={!loading}
      />

      <ScrollView style={styles.scrollView}>
        {loading ? (
          <ThemedText style={styles.message}>Loading users...</ThemedText>
        ) : filteredUsers.length > 0 ? (
          filteredUsers.map(renderUser)
        ) : (
          <ThemedText style={styles.message}>{search ? "No users found" : "No users available"}</ThemedText>
        )}
      </ScrollView>

      <View style={styles.footer}>
        {multiple && (
          <>
            <ThemedText style={styles.selectedCount}>{selectedUsers.length} selected</ThemedText>
            <View style={styles.footerButtons}>
              <Button
                text="Cancel"
                style={[styles.cancelButton, { borderColor: colors.border }]}
                textStyle={{ color: colors.text }}
                onPress={onCancel}
              />
              <Button
                text="Confirm"
                style={styles.confirmButton}
                onPress={handleConfirmMultiple}
                enabled={selectedUsers.length > 0}
              />
            </View>
          </>
        )}
        {!multiple && onCancel && (
          <Button
            text="Cancel"
            style={[styles.cancelButton, { borderColor: colors.border }]}
            textStyle={{ color: colors.text }}
            onPress={onCancel}
          />
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 8,
    gap: 8,
  },
  searchInput: {
    height: 36,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
  },
  scrollView: {
    maxHeight: 350,
  },
  message: {
    opacity: 0.7,
    alignSelf: "center",
    marginVertical: 20,
  },
  userItem: {
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    backgroundColor: "transparent",
  },
  userContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },
  userInfo: {
    flex: 1,
    gap: 2,
  },
  userDetail: {
    fontSize: 12,
    opacity: 0.6,
  },
  userRole: {
    fontSize: 11,
    opacity: 0.5,
    fontStyle: "italic",
  },
  footer: {
    marginTop: 8,
  },
  selectedCount: {
    opacity: 0.7,
    marginBottom: 8,
  },
  footerButtons: {
    flexDirection: "row",
    gap: 8,
    justifyContent: "flex-end",
  },
  cancelButton: {
    height: 32,
    paddingHorizontal: 16,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
  confirmButton: {
    height: 32,
    paddingHorizontal: 16,
  },
});
